/**
 * AI training data API client wrapping the AI service's /training-data/*
 * endpoints (via aiApi). Supports scraping a URL for training data, plus
 * upload/edit/delete/fetch of submission-verification and AI-search training
 * data sets. Consumed by admin tooling (PaginatedList,
 * TrainingDataCreateDisplay).
 */
import { aiApi } from "@/lib/axios";
import {
    SubmissionVerificationExtractedData,
    SubmissionVerificationTrainingDataSetUpload,
    SubmissionVerificationTrainingDataSetDisplay,
    SearchTrainingDataSetUpload,
    SearchTrainingDataSetDisplay,
    SearchResultsExtractedData,
} from "@/types/trainingData";

export const TrainingDataAPI = {
    // Scraping
    scrapeTrainingDataUrl: async (
        url: string,
        dataType: string
    ): Promise<{
        html: string;
        extractedData: SubmissionVerificationExtractedData | SearchResultsExtractedData;
    }> => {
        const { data } = await aiApi.post(`/training-data/${dataType}/scrape-url`, {
            url,
        });
        return data;
    },

    // Submission Verification
    uploadSubmissionVerificationTrainingDataSet: async (
        data: SubmissionVerificationTrainingDataSetUpload
    ): Promise<void> => {
        await aiApi.post("/training-data/submission-verification", data);
    },
    editSubmissionVerificationTrainingDataSetExtractedData: async (
        label: string,
        extractedData: SubmissionVerificationExtractedData
    ): Promise<void> => {
        await aiApi.post(
            `/training-data/submission-verification/${label}/edit-extracted-data`,
            extractedData
        );
    },
    deleteSubmissionVerificationTrainingDataSet: async (label: string): Promise<void> => {
        await aiApi.delete(`/training-data/submission-verification/${label}`);
    },
    fetchSubmissionVerificationTrainingData: async (
        domain?: string
    ): Promise<SubmissionVerificationTrainingDataSetDisplay[]> => {
        const { data } = await aiApi.get<{
            submissionVerificationTrainingDataSets: SubmissionVerificationTrainingDataSetDisplay[];
        }>("/training-data/submission-verification", {
            params: {
                domain,
            },
        });
        return data.submissionVerificationTrainingDataSets;
    },

    // AI Search
    uploadSearchTrainingDataSet: async (
        trainingData: SearchTrainingDataSetUpload
    ): Promise<void> => {
        await aiApi.post(`/training-data/search`, trainingData);
    },
    deleteSearchTrainingDataSet: async (label: string): Promise<void> => {
        await aiApi.delete(`/training-data/search/${label}`);
    },
    fetchSearchTrainingData: async (): Promise<SearchTrainingDataSetDisplay[]> => {
        const { data } = await aiApi.get<{
            searchTrainingDataSets: SearchTrainingDataSetDisplay[];
        }>(`/training-data/search`);
        return data.searchTrainingDataSets;
    },
};
